/**
 * Profile API hooks
 *
 * Hooks for fetching and updating the current user's profile.
 * Uses snake_case types to match backend API response directly.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { useAuthStore } from '@/stores/authStore'
import { identityKeys } from './useIdentity'
import { userManagementKeys } from './useUserManagement'

// ============================================================================
// Types
// ============================================================================

/**
 * Profile update input - snake_case matching backend
 */
export interface ProfileUpdateInput {
  first_name: string
  last_name: string
  phone_number?: string
  new_password?: string
  locale?: string
  two_factor_enabled?: boolean
}

/**
 * Profile update response - matches backend User model
 */
export interface ProfileUpdateResponse {
  id: number
  first_name: string
  last_name: string
  email: string
  phone_number?: string
  locale?: string
  two_factor_enabled: boolean
  is_active: boolean
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook to update the current user's profile
 *
 * Uses the react-admin format endpoint: /users/ra/update/{id}
 */
export function useUpdateProfile() {
  const queryClient = useQueryClient()
  const user = useAuthStore((state) => state.user)

  return useMutation({
    mutationFn: async (data: ProfileUpdateInput): Promise<ProfileUpdateResponse> => {
      if (!user?.id) {
        throw new Error('User not authenticated')
      }

      // Use the same endpoint as the original Profile.tsx
      const response = await api.post(`/users/ra/update/${user.id}`, {
        ...data,
        id: user.id, // Required by the backend UserUpdate schema
        email: user.email, // Email is not editable, pass it as-is
      })

      return response.data.data || response.data
    },
    onSuccess: () => {
      // Invalidate identity query to refresh user data
      queryClient.invalidateQueries({ queryKey: identityKeys.me() })
      // Invalidate user detail query (used by useUser in ProfilePage)
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: userManagementKeys.detail(Number(user.id)) })
      }
    },
  })
}

export default useUpdateProfile
